// ─────────────────────────────────────────────────────────────────────────────
//  components/pixel-bg.js — Pluie de pixels tombants (background animé)
//
//  Portage V1 → V2 :
//  - IIFE → ES module (export de fonction)
//  - Couleur inversée : noir (#000) en V1 sur fond blanc → blanc (#fff) en V2 sur fond noir
//  - Même algorithme, même perf, même accessibilité (prefers-reduced-motion)
//
//  Comment ça marche :
//  On crée N divs positionnés fixed avec une animation CSS `fallingPixel`
//  (translateY de -taille → 100vh). Chaque pixel a :
//    - Une position X aléatoire (left: 0-100%)
//    - Une durée aléatoire (15-35s) → vitesses différentes
//    - Un délai négatif aléatoire → ils ne démarrent pas tous en même temps
//    - Une opacité aléatoire (0.08-0.25) → discrets sur le fond noir
//
//  Pourquoi des délais négatifs ?
//  Un délai de -10s sur une animation de 30s = l'animation démarre comme si
//  elle était déjà en cours depuis 10s. Résultat : à l'ouverture de la page,
//  des pixels sont déjà en train de tomber à mi-chemin, pas tous en haut.
//
//  Pourquoi z-index: -1 ?
//  Le conteneur .pixel-bg est derrière tout le contenu mais devant le body.
//  pointer-events: none → on peut cliquer à travers les pixels.
// ─────────────────────────────────────────────────────────────────────────────

// Tailles des pixels selon la largeur d'écran
const SIZES   = { xs: 16, sm: 18, md: 20, lg: 24 };
const COUNTS  = { mobile: 8, tablet: 20, desktop: 60 };
const DURATION = { min: 15, max: 35 };

// Couleur V2 : blanc semi-transparent sur fond noir
// On utilise la variable CSS pour permettre un override facile
const COLOR_VAR = '--pixel-rain-color';
const COLOR_DEFAULT = 'rgba(255,255,255,1)';  // opacité gérée par le JS

let _container = null;
let _resizeTimer = null;
let _active = false;

// ── Initialisation ────────────────────────────────────────────────────────────
//
// Appeler init() une fois depuis main.js.
// La fonction crée le conteneur et lance la pluie de pixels.

export function initPixelBg() {
  // Respecte prefers-reduced-motion — accessibilité
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Crée le conteneur s'il n'existe pas
  if (!_container) {
    _container = document.createElement('div');
    _container.className = 'pixel-bg';
    _container.setAttribute('aria-hidden', 'true');
    document.body.prepend(_container);  // premier enfant du body
  }

  // Injecte les @keyframes une seule fois dans le <head>
  if (!document.getElementById('pixel-rain-keyframes')) {
    const style = document.createElement('style');
    style.id = 'pixel-rain-keyframes';
    style.textContent = `
      @keyframes fallingPixel {
        0%   { transform: translateY(0); }
        100% { transform: translateY(100vh); }
      }
    `;
    document.head.appendChild(style);
  }

  _buildPixels();
  _active = true;

  // Recrée les pixels quand la fenêtre est redimensionnée (debounce 300ms)
  window.addEventListener('resize', _onResize);
}

// ── Destruction ───────────────────────────────────────────────────────────────
// Utile si on veut couper l'animation (ex: page admin sans déco)

export function destroyPixelBg() {
  _active = false;
  window.removeEventListener('resize', _onResize);
  if (_container) {
    _container.remove();
    _container = null;
  }
}

// ── Construction des pixels ───────────────────────────────────────────────────

function _buildPixels() {
  if (!_container) return;

  // Vide les pixels existants
  _container.innerHTML = '';

  const size  = _getSize();
  const count = _getCount();

  for (let i = 0; i < count; i++) {
    const px = document.createElement('div');
    px.className = 'falling-pixel';

    const x        = Math.random() * 100;                           // position horizontale %
    const duration = DURATION.min + Math.random() * (DURATION.max - DURATION.min);
    const delay    = -(Math.random() * duration);                   // délai négatif = déjà en vol
    const opacity  = 0.08 + Math.random() * 0.17;                  // 0.08 à 0.25 — discret

    px.style.cssText = [
      `position:fixed`,
      `top:${-size}px`,
      `left:${x}%`,
      `margin-left:${-size / 2}px`,
      `width:${size}px`,
      `height:${size}px`,
      `background:var(${COLOR_VAR}, ${COLOR_DEFAULT})`,
      `opacity:${opacity}`,
      `z-index:-1`,
      `pointer-events:none`,
      `animation:fallingPixel ${duration}s linear ${delay}s infinite`,
    ].join(';');

    _container.appendChild(px);
  }
}

function _getSize() {
  const w = window.innerWidth;
  if (w > 1024) return SIZES.lg;
  if (w > 768)  return SIZES.md;
  if (w > 480)  return SIZES.sm;
  return SIZES.xs;
}

function _getCount() {
  const w = window.innerWidth;
  if (w > 1024) return COUNTS.desktop;
  if (w > 768)  return COUNTS.tablet;
  return COUNTS.mobile;
}

function _onResize() {
  clearTimeout(_resizeTimer);
  _resizeTimer = setTimeout(_buildPixels, 300);
}
